const {
  Configuration,
  ProductOverviewApi,
  StyleDetail01Api,
  StyleDetail03Api,
  DefaultPageApi,
  CommonApi,
  MemberOrderApi,
  NewebPayApi,
} = require("./api/index");
const { refreshJWTIfNeeded } = require("../utils/Jwt");
class AddChineseHeaderMiddleware {
  constructor(headerKey, headerValue) {
    this.headerKey = headerKey; // 参数名
    this.headerValue = headerValue; // 参数值（可以是中文）
  }

  async pre(context) {
    const { url, init } = context;

    const updatedHeaders = {
      ...init.headers,
      [this.headerKey]: encodeURIComponent(this.headerValue), // 添加中文参数到 Header
    };

    return {
      url,
      init: {
        ...init,
        headers: updatedHeaders,
      },
    };
  }
}

// 每次 request 動態從 localStorage 讀取會員資訊並加入 header
// 對應後端 GetMemberNo() / GetMemberId() / GetMemberName()
class MemberNoHeaderMiddleware {
  async pre(context) {
    const { url, init } = context;
    let extraHeaders = {};
    try {
      const userInfo = localStorage.getItem("rw_usr_prof");
      if (userInfo) {
        const parsed = JSON.parse(userInfo);
        if (parsed.memberNo) extraHeaders["memberNo"] = String(parsed.memberNo);
        if (parsed.memberNumber)
          extraHeaders["memberId"] = String(parsed.memberNumber);
        // HTTP Header 只允許 ISO-8859-1，中文需編碼後才能放入 Header
        if (parsed.memberName)
          extraHeaders["memberName"] = encodeURIComponent(parsed.memberName);
      }
    } catch (e) {}

    if (Object.keys(extraHeaders).length === 0) return context;

    const updatedHeaders = { ...init.headers, ...extraHeaders };
    return { url, init: { ...init, headers: updatedHeaders } };
  }
}

class ApiService {
  constructor(apiKey = "", basePath = process.env.REACT_APP_API_URL) {
    // 若沒傳入 apiKey，嘗試從 localStorage 讀取已存的 token（頁面重整後保持登入狀態）
    if (!apiKey) {
      const stored = localStorage.getItem("rw_app_tk");
      if (stored) {
        apiKey = stored.startsWith("Bearer ") ? stored : `Bearer ${stored}`;
      }
    }
    const chineseMiddleware = new AddChineseHeaderMiddleware(
      "sysName",
      process.env.REACT_APP_API_KEY,
    );
    const memberNoMiddleware = new MemberNoHeaderMiddleware();
    const configuration = new Configuration({
      apiKey: apiKey,
      basePath: basePath,
      middleware: [chineseMiddleware, memberNoMiddleware],
    });
    this.apiKey = apiKey; // 存储初始 apiKey
    this.basePath = basePath;
    this.middleware = [chineseMiddleware, memberNoMiddleware];
    //api
    this.commonApi = new CommonApi(configuration);
    this.productOverviewApi = this.createApiProxy(
      new ProductOverviewApi(configuration),
    );
    this.styleDetail01Api = this.createApiProxy(
      new StyleDetail01Api(configuration),
    );
    this.styleDetail03Api = this.createApiProxy(
      new StyleDetail03Api(configuration),
    );
    this.defaultPageApi = this.createApiProxy(
      new DefaultPageApi(configuration),
    );
    this.memberOrderApi = this.createApiProxy(
      new MemberOrderApi(configuration),
    );
    this.newebPayApi = this.createApiProxy(new NewebPayApi(configuration));
  }
  addFixedParametersMiddleware = {
    pre: async (context) => {
      // 添加固定的查詢參數
      const url = new URL(context.url);
      url.searchParams.append("fixedParam", "fixedValue"); // 固定查詢參數

      // 添加固定的標頭
      const headers = {
        ...context.init.headers,
        "X-Custom-Header": "CustomValue", // 固定標頭
      };

      return {
        url: url.toString(),
        init: {
          ...context.init,
          headers,
        },
      };
    },
  };

  // 更新 apiKey
  updateApiKey(newApiKey) {
    newApiKey = "Bearer " + newApiKey;
    this.apiKey = newApiKey; // 更新 apiKey
    localStorage.setItem("rw_app_tk", newApiKey); // 使用 AuthManager 的 key

    const chineseMiddleware = new AddChineseHeaderMiddleware(
      "sysName",
      process.env.REACT_APP_API_KEY,
    );
    const memberNoMiddleware = new MemberNoHeaderMiddleware();
    const configuration = new Configuration({
      apiKey: newApiKey,
      basePath: this.basePath,
      middleware: [chineseMiddleware, memberNoMiddleware],
    });
    //api - 更新所有 API 實例
    this.commonApi = new CommonApi(configuration);
    this.productOverviewApi = this.createApiProxy(
      new ProductOverviewApi(configuration),
    );
    this.styleDetail01Api = this.createApiProxy(
      new StyleDetail01Api(configuration),
    );
    this.styleDetail03Api = this.createApiProxy(
      new StyleDetail03Api(configuration),
    );
    this.defaultPageApi = this.createApiProxy(
      new DefaultPageApi(configuration),
    );
    this.memberOrderApi = this.createApiProxy(
      new MemberOrderApi(configuration),
    );
    this.newebPayApi = this.createApiProxy(new NewebPayApi(configuration));
  }

  // 創建 API 的代理（加入 JWT 自動刷新）
  createApiProxy(apiInstance) {
    const handler = {
      get: (target, prop) => {
        if (typeof target[prop] === "function") {
          return async (...args) => {
            try {
              // 先檢查和刷新 JWT
              await refreshJWTIfNeeded();
              // 然後調用原始方法
              return await target[prop](...args);
            } catch (error) {
              // 如果是 401 錯誤，可能是 token 過期
              if (error.status === 401 || error.response?.status === 401) {
                console.error("❌ API 呼叫失敗 - 認證錯誤:", error);
                // 導入並執行強制登出
                const { forceLogout } = await import("../utils/Jwt");
                forceLogout();
                throw error;
              }
              throw error;
            }
          };
        }
        return target[prop]; // 返回屬性或值
      },
    };
    return new Proxy(apiInstance, handler);
  }

  static getInstance(apiKey = "", basePath = process.env.REACT_APP_API_URL) {
    if (!ApiService.instance) {
      ApiService.instance = new ApiService(apiKey, basePath);
    }
    return ApiService.instance;
  }
}

export default ApiService.getInstance();
